"use client";

import { Dialog } from "@base-ui/react/dialog";
import {
  ArrowClockwise,
  CaretLeft,
  CaretRight,
  ImageBroken,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  X,
} from "@phosphor-icons/react";
import {
  cloneElement,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import styles from "./image-lightbox.module.css";

export type ImageLightboxItem = {
  id: string;
  src: string | null | undefined;
  alt?: string;
};

export type ImageLightboxAction =
  | "previous"
  | "next"
  | "zoomIn"
  | "zoomOut"
  | "resetZoom"
  | "close";

export type ImageLightboxHandle = {
  popup: HTMLDivElement | null;
  previous: () => void;
  next: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  canPrevious: boolean;
  canNext: boolean;
  canZoomIn: boolean;
  canZoomOut: boolean;
};

export type ImageLightboxLabels = {
  previous: string;
  next: string;
  zoomIn: string;
  zoomOut: string;
  resetZoom: string;
  close: string;
  loading: string;
  unavailable: string;
  retry: string;
  retrying: string;
  instructions: string;
  viewport: string;
  imageLabel: (index: number, total: number) => string;
  imageAlt: (index: number) => string;
  zoomLabel: (percent: number) => string;
};

export type ImageLightboxProps = {
  items: readonly ImageLightboxItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialIndex?: number;
  finalFocus?: ComponentProps<typeof Dialog.Popup>["finalFocus"];
  loop?: boolean;
  fit?: "contain" | "width";
  keyboardShortcuts?: boolean;
  ref?: Ref<ImageLightboxHandle>;
  labels?: Partial<ImageLightboxLabels>;
  onRetry?: (item: ImageLightboxItem) => void | Promise<void>;
  renderAction?: (props: {
    action: ImageLightboxAction;
    disabled: boolean;
    button: ReactElement;
  }) => ReactNode;
};

const defaultLabels: ImageLightboxLabels = {
  previous: "上一张图片",
  next: "下一张图片",
  zoomIn: "放大图片",
  zoomOut: "缩小图片",
  resetZoom: "重置缩放",
  close: "关闭图片",
  loading: "正在加载图片…",
  unavailable: "图片暂时不可用",
  retry: "重新加载",
  retrying: "正在重试…",
  instructions:
    "使用左右方向键切换图片，加减键缩放，0 重置，Esc 关闭。聚焦图片画布后可用方向键、PageUp、PageDown、Home 和 End 查看图片各处。双击放大，放大后拖动查看。",
  viewport: "图片画布",
  imageLabel: (index, total) => `图片 ${index}，共 ${total} 张`,
  imageAlt: (index) => `内容图片 ${index}`,
  zoomLabel: (percent) => `${percent}%`,
};

type Point = { x: number; y: number };
type Anchor = { x: number; y: number; viewportX: number; viewportY: number };
type ViewState = {
  identity: string;
  generation: number;
  scale: number;
  width: number;
  height: number;
  status: "loading" | "loaded" | "error";
  retrying: boolean;
  attempt: number;
};
type Gesture = {
  points: Map<number, Point>;
  mode: "idle" | "swipe" | "pan" | "pinch" | "ignore";
  start: Point;
  scroll: Point;
  scale: number;
  distance: number;
  anchor: Anchor | null;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a: Point, b: Point) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
function localPoint(element: HTMLDivElement, clientX: number, clientY: number): Point {
  const rect = element.getBoundingClientRect();
  return {
    x: (clientX - rect.left) * (rect.width ? element.clientWidth / rect.width : 1),
    y: (clientY - rect.top) * (rect.height ? element.clientHeight / rect.height : 1),
  };
}
const initialView = (
  identity: string,
  src: string | null | undefined,
  generation = 0,
): ViewState => ({
  identity,
  generation,
  scale: 1,
  width: 0,
  height: 0,
  status: src ? "loading" : "error",
  retrying: false,
  attempt: 0,
});

/** A self-contained image viewer. Hosts resolve URLs, permissions and product shortcuts. */
export function ImageLightbox({
  items,
  open,
  onOpenChange,
  initialIndex = 0,
  finalFocus,
  loop = true,
  fit = "contain",
  keyboardShortcuts = true,
  ref,
  labels: customLabels,
  onRetry,
  renderAction,
}: ImageLightboxProps) {
  const labels = { ...defaultLabels, ...customLabels };
  const firstIndex = clamp(
    Number.isFinite(initialIndex) ? Math.trunc(initialIndex) : 0,
    0,
    Math.max(0, items.length - 1),
  );
  const [selection, setSelection] = useState(() => ({
    open,
    generation: 0,
    id: items[firstIndex]?.id,
    index: firstIndex,
  }));
  if (selection.open !== open) {
    setSelection({
      open,
      generation: selection.generation + (open ? 1 : 0),
      id: open ? items[firstIndex]?.id : selection.id,
      index: open ? firstIndex : selection.index,
    });
  }
  const foundIndex = items.findIndex((item) => item.id === selection.id);
  const index =
    foundIndex >= 0 ? foundIndex : clamp(selection.index, 0, Math.max(0, items.length - 1));
  const current = items[index];
  const identity = JSON.stringify([selection.generation, open, current?.id, current?.src, fit]);
  const [view, setView] = useState(() => initialView(identity, current?.src));
  if (view.identity !== identity) setView(initialView(identity, current?.src, view.generation + 1));
  const viewRef = useRef(view);
  viewRef.current = view;
  const [popup, setPopup] = useState<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const [bounds, setBounds] = useState({ width: 0, height: 0 });
  const [dragging, setDragging] = useState(false);
  const pendingAnchor = useRef<Anchor | null>(null);
  const retryLock = useRef<{ generation: number; request: number } | null>(null);
  const retryRequest = useRef(0);
  const emptyCloseSent = useRef(false);
  const touchTime = useRef(0);
  const lastTap = useRef<{ time: number; point: Point } | null>(null);
  const gesture = useRef<Gesture>({
    points: new Map(),
    mode: "idle",
    start: { x: 0, y: 0 },
    scroll: { x: 0, y: 0 },
    scale: 1,
    distance: 0,
    anchor: null,
  });

  useEffect(() => {
    if (!open || items.length > 0) {
      emptyCloseSent.current = false;
      return;
    }
    if (!emptyCloseSent.current) {
      emptyCloseSent.current = true;
      onOpenChange(false);
    }
  }, [open, items.length, onOpenChange]);

  useEffect(() => {
    if (current && (selection.id !== current.id || selection.index !== index))
      setSelection((previous) => ({ ...previous, id: current.id, index }));
  }, [current, index, selection.id, selection.index]);

  useLayoutEffect(() => {
    if (!viewport) return;
    const measure = () => setBounds({ width: viewport.clientWidth, height: viewport.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    // Scrollbars created by zoom must not cancel the gesture that created them.
    observer.observe(viewport, { box: "border-box" });
    return () => observer.disconnect();
  }, [viewport]);

  useLayoutEffect(() => {
    const active = gesture.current;
    const pointerIds = [...active.points.keys()];
    active.points.clear();
    active.mode = "idle";
    active.anchor = null;
    for (const pointerId of pointerIds) {
      if (viewport?.hasPointerCapture(pointerId)) viewport.releasePointerCapture(pointerId);
    }
    pendingAnchor.current = null;
    lastTap.current = null;
    retryLock.current = null;
    setDragging(false);
    if (viewport) {
      viewport.scrollLeft = 0;
      viewport.scrollTop = 0;
    }
  }, [identity, view.attempt, open, viewport]);

  useLayoutEffect(() => {
    // A rotation or viewport resize invalidates pointer coordinates captured earlier.
    cancelGesture();
    pendingAnchor.current = null;
  }, [bounds.width, bounds.height]);

  const padding = bounds.width < 640 ? 12 : 32;
  const availableWidth = Math.max(1, bounds.width - padding * 2);
  const availableHeight = Math.max(1, bounds.height - padding * 2);
  const baseRatio =
    view.width && view.height
      ? fit === "width"
        ? availableWidth / view.width
        : Math.min(1, availableWidth / view.width, availableHeight / view.height)
      : 0;
  const imageWidth = view.width * baseRatio * view.scale;
  const imageHeight = view.height * baseRatio * view.scale;
  const surfaceWidth = Math.max(bounds.width, imageWidth + padding * 2);
  const surfaceHeight = Math.max(bounds.height, imageHeight + padding * 2);
  const imageLeft = (surfaceWidth - imageWidth) / 2;
  const imageTop = (surfaceHeight - imageHeight) / 2;
  const minScale = fit === "width" ? 0.5 : 1;
  const ready = Boolean(open && current && view.status === "loaded");
  const canPrevious = Boolean(open && items.length > 1 && (loop || index > 0));
  const canNext = Boolean(open && items.length > 1 && (loop || index < items.length - 1));
  const canZoomIn = ready && view.scale < 4;
  const canZoomOut = ready && view.scale > minScale;

  useLayoutEffect(() => {
    const anchor = pendingAnchor.current;
    if (!viewport || !anchor || !imageWidth || !imageHeight) return;
    viewport.scrollLeft = imageLeft + anchor.x * imageWidth - anchor.viewportX;
    viewport.scrollTop = imageTop + anchor.y * imageHeight - anchor.viewportY;
    pendingAnchor.current = null;
  }, [viewport, imageWidth, imageHeight, imageLeft, imageTop]);

  function anchorAt(x = bounds.width / 2, y = bounds.height / 2): Anchor {
    return {
      x: imageWidth ? clamp(((viewport?.scrollLeft ?? 0) + x - imageLeft) / imageWidth, 0, 1) : 0.5,
      y: imageHeight ? clamp(((viewport?.scrollTop ?? 0) + y - imageTop) / imageHeight, 0, 1) : 0.5,
      viewportX: x,
      viewportY: y,
    };
  }

  function changeScale(next: number, anchor = anchorAt()) {
    if (!ready) return;
    const scale = Math.round(clamp(next, minScale, 4) * 1000) / 1000;
    if (scale === viewRef.current.scale) return;
    pendingAnchor.current = anchor;
    setView((previous) => (previous.identity === identity ? { ...previous, scale } : previous));
  }

  function select(offset: number) {
    if ((offset < 0 && !canPrevious) || (offset > 0 && !canNext)) return;
    const nextIndex = loop ? (index + offset + items.length) % items.length : index + offset;
    setSelection((previous) => ({ ...previous, id: items[nextIndex]?.id, index: nextIndex }));
  }

  function zoomIn() {
    if (canZoomIn) changeScale(viewRef.current.scale + 0.5);
  }
  function zoomOut() {
    if (canZoomOut) changeScale(viewRef.current.scale - 0.5);
  }
  function resetZoom() {
    changeScale(1);
  }
  function toggleZoom(x?: number, y?: number) {
    changeScale(viewRef.current.scale === 1 ? 2 : 1, anchorAt(x, y));
  }

  useImperativeHandle(ref, () => ({
    popup,
    previous: () => select(-1),
    next: () => select(1),
    zoomIn,
    zoomOut,
    resetZoom,
    canPrevious,
    canNext,
    canZoomIn,
    canZoomOut,
  }));

  function cancelGesture() {
    const active = gesture.current;
    const pointerIds = [...active.points.keys()];
    active.points.clear();
    active.mode = "idle";
    active.anchor = null;
    lastTap.current = null;
    setDragging(false);
    for (const pointerId of pointerIds) {
      if (viewport?.hasPointerCapture(pointerId)) viewport.releasePointerCapture(pointerId);
    }
  }

  function pointerDown(event: PointerEvent<HTMLDivElement>) {
    if (
      !ready ||
      event.button !== 0 ||
      (event.target instanceof HTMLElement && event.target.closest("button, a, input"))
    )
      return;
    const active = gesture.current;
    const point = localPoint(event.currentTarget, event.clientX, event.clientY);
    active.points.set(event.pointerId, point);
    event.currentTarget.setPointerCapture(event.pointerId);
    if (active.mode === "ignore") return;
    if (active.points.size === 1) {
      active.mode =
        view.scale > 1 || fit === "width"
          ? "pan"
          : event.pointerType === "touch" && event.isPrimary
            ? "swipe"
            : "idle";
      active.start = point;
      active.scroll = { x: event.currentTarget.scrollLeft, y: event.currentTarget.scrollTop };
      setDragging(active.mode === "pan");
    } else if (active.points.size === 2) {
      const [a, b] = [...active.points.values()];
      const center = midpoint(a!, b!);
      active.mode = "pinch";
      active.scale = viewRef.current.scale;
      active.distance = Math.max(1, distance(a!, b!));
      active.anchor = anchorAt(center.x, center.y);
      lastTap.current = null;
      setDragging(true);
    }
    if (event.pointerType === "mouse") event.preventDefault();
  }

  function pointerMove(event: PointerEvent<HTMLDivElement>) {
    const active = gesture.current;
    if (!active.points.has(event.pointerId)) return;
    const point = localPoint(event.currentTarget, event.clientX, event.clientY);
    active.points.set(event.pointerId, point);
    if (active.mode === "pinch" && active.points.size >= 2) {
      const [a, b] = [...active.points.values()];
      const center = midpoint(a!, b!);
      if (active.anchor)
        changeScale((active.scale * distance(a!, b!)) / active.distance, {
          ...active.anchor,
          viewportX: center.x,
          viewportY: center.y,
        });
    } else if (active.mode === "pan") {
      event.currentTarget.scrollLeft = active.scroll.x - (point.x - active.start.x);
      event.currentTarget.scrollTop = active.scroll.y - (point.y - active.start.y);
    }
  }

  function pointerUp(event: PointerEvent<HTMLDivElement>) {
    const active = gesture.current;
    if (!active.points.has(event.pointerId)) return;
    const wasPinch = active.mode === "pinch" || active.mode === "ignore";
    const point = localPoint(event.currentTarget, event.clientX, event.clientY);
    const dx = point.x - active.start.x;
    const dy = point.y - active.start.y;
    if (
      active.mode === "swipe" &&
      fit === "contain" &&
      viewRef.current.scale === 1 &&
      Math.abs(dx) > 60 &&
      Math.abs(dx) > Math.abs(dy) * 1.5
    ) {
      select(dx > 0 ? -1 : 1);
      lastTap.current = null;
    } else if (!wasPinch && event.pointerType === "touch" && Math.hypot(dx, dy) < 12) {
      const now = Date.now();
      const previousTap = lastTap.current;
      if (previousTap && now - previousTap.time < 300 && distance(previousTap.point, point) < 32) {
        toggleZoom(point.x, point.y);
        lastTap.current = null;
      } else lastTap.current = { time: now, point };
      touchTime.current = now;
    }
    active.points.delete(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    active.mode = wasPinch && active.points.size ? "ignore" : "idle";
    setDragging(false);
  }

  async function retry() {
    if (!current || retryLock.current?.generation === view.generation) return;
    const item = current;
    const requestIdentity = identity;
    const generation = view.generation;
    const token = { generation, request: ++retryRequest.current };
    retryLock.current = token;
    const isCurrent = () =>
      retryLock.current === token &&
      viewRef.current.identity === requestIdentity &&
      viewRef.current.generation === generation;
    setView((previous) =>
      previous.generation === generation ? { ...previous, retrying: true } : previous,
    );
    try {
      await onRetry?.(item);
      if (!isCurrent()) return;
      setView((previous) =>
        previous.generation === generation
          ? {
              ...initialView(requestIdentity, item.src, generation + 1),
              attempt: previous.attempt + 1,
            }
          : previous,
      );
    } catch {
      if (isCurrent())
        setView((previous) =>
          previous.generation === generation
            ? { ...previous, retrying: false, status: "error" }
            : previous,
        );
    } finally {
      if (retryLock.current === token) retryLock.current = null;
    }
  }

  function action(actionName: ImageLightboxAction, disabled: boolean, button: ReactElement) {
    return renderAction
      ? renderAction({ action: actionName, disabled, button })
      : cloneElement(button as ReactElement<{ title?: string }>, { title: labels[actionName] });
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Popup
          ref={setPopup}
          finalFocus={finalFocus}
          className={styles.popup}
          data-image-lightbox=""
          data-scale={view.scale}
          data-fit={fit}
          onKeyDown={(event) => {
            if (
              !open ||
              event.defaultPrevented ||
              event.nativeEvent.isComposing ||
              event.nativeEvent.keyCode === 229 ||
              !event.currentTarget.contains(event.target as Node)
            )
              return;
            const target = event.target as HTMLElement;
            if (
              target.isContentEditable ||
              target.closest("input, textarea, select, [role='textbox']")
            )
              return;
            if (!keyboardShortcuts) {
              // Base UI otherwise stops composite keys at its popup. Product
              // command routing must receive them, without losing Escape handling.
              if (
                [
                  "ArrowLeft",
                  "ArrowRight",
                  "ArrowUp",
                  "ArrowDown",
                  "Home",
                  "End",
                  "PageUp",
                  "PageDown",
                ].includes(event.key)
              )
                event.preventBaseUIHandler();
              return;
            }
            if (event.altKey || event.ctrlKey || event.metaKey) return;
            let handled = true;
            if (event.key === "ArrowLeft") select(-1);
            else if (event.key === "ArrowRight") select(1);
            else if (event.key === "+" || event.key === "=") zoomIn();
            else if (event.key === "-" || event.key === "_") zoomOut();
            else if (event.key === "0") resetZoom();
            else handled = false;
            if (handled) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          <Dialog.Title className={styles.srOnly}>
            {labels.imageLabel(current ? index + 1 : 0, items.length)}
          </Dialog.Title>
          <Dialog.Description className={styles.srOnly}>{labels.instructions}</Dialog.Description>
          <header className={styles.header}>
            <span className={styles.counter} aria-hidden="true">
              {current ? index + 1 : 0}
              <span> / {items.length}</span>
            </span>
            <div className={styles.toolbar}>
              {action(
                "zoomOut",
                !canZoomOut,
                <button
                  type="button"
                  className={styles.button}
                  disabled={!canZoomOut}
                  aria-label={labels.zoomOut}
                  data-lightbox-action="zoomOut"
                  onClick={zoomOut}
                >
                  <MagnifyingGlassMinus size={20} aria-hidden="true" />
                </button>,
              )}
              {action(
                "resetZoom",
                !ready || view.scale === 1,
                <button
                  type="button"
                  className={`${styles.button} ${styles.zoomValue}`}
                  disabled={!ready || view.scale === 1}
                  aria-label={labels.resetZoom}
                  data-lightbox-action="resetZoom"
                  onClick={resetZoom}
                >
                  {labels.zoomLabel(Math.round(view.scale * 100))}
                </button>,
              )}
              {action(
                "zoomIn",
                !canZoomIn,
                <button
                  type="button"
                  className={styles.button}
                  disabled={!canZoomIn}
                  aria-label={labels.zoomIn}
                  data-lightbox-action="zoomIn"
                  onClick={zoomIn}
                >
                  <MagnifyingGlassPlus size={20} aria-hidden="true" />
                </button>,
              )}
              <span className={styles.divider} aria-hidden="true" />
              {action(
                "close",
                false,
                <Dialog.Close
                  className={styles.button}
                  aria-label={labels.close}
                  data-lightbox-action="close"
                >
                  <X size={21} aria-hidden="true" />
                </Dialog.Close>,
              )}
            </div>
          </header>
          <div
            ref={setViewport}
            className={styles.viewport}
            tabIndex={0}
            role="region"
            aria-label={labels.viewport}
            data-lightbox-viewport=""
            data-draggable={(ready && (view.scale > 1 || fit === "width")) || undefined}
            data-dragging={dragging || undefined}
            onKeyDown={(event) => {
              if (
                event.target !== event.currentTarget ||
                event.defaultPrevented ||
                event.nativeEvent.isComposing ||
                event.nativeEvent.keyCode === 229 ||
                event.altKey ||
                event.ctrlKey ||
                event.metaKey
              )
                return;
              const element = event.currentTarget;
              const horizontal = element.scrollWidth > element.clientWidth + 1;
              const vertical = element.scrollHeight > element.clientHeight + 1;
              let handled = false;
              if (horizontal && event.key === "ArrowLeft") {
                element.scrollLeft -= 50;
                handled = true;
              } else if (horizontal && event.key === "ArrowRight") {
                element.scrollLeft += 50;
                handled = true;
              } else if (vertical && event.key === "ArrowUp") {
                element.scrollTop -= 50;
                handled = true;
              } else if (vertical && event.key === "ArrowDown") {
                element.scrollTop += 50;
                handled = true;
              } else if (vertical && event.key === "PageUp") {
                element.scrollTop -= element.clientHeight * 0.9;
                handled = true;
              } else if (vertical && event.key === "PageDown") {
                element.scrollTop += element.clientHeight * 0.9;
                handled = true;
              } else if ((horizontal || vertical) && event.key === "Home") {
                element.scrollLeft = 0;
                element.scrollTop = 0;
                handled = true;
              } else if ((horizontal || vertical) && event.key === "End") {
                element.scrollLeft = element.scrollWidth;
                element.scrollTop = element.scrollHeight;
                handled = true;
              }
              if (handled) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onPointerDown={pointerDown}
            onPointerMove={pointerMove}
            onPointerUp={pointerUp}
            onPointerCancel={cancelGesture}
            onLostPointerCapture={(event) => {
              if (gesture.current.points.has(event.pointerId)) cancelGesture();
            }}
            onDoubleClick={(event) => {
              if (
                Date.now() - touchTime.current < 500 ||
                (event.target instanceof HTMLElement && event.target.closest("button"))
              )
                return;
              const point = localPoint(event.currentTarget, event.clientX, event.clientY);
              toggleZoom(point.x, point.y);
            }}
          >
            <div
              className={styles.surface}
              data-lightbox-surface=""
              style={{ width: surfaceWidth || "100%", height: surfaceHeight || "100%" }}
            >
              {current?.src ? (
                <img
                  key={`${identity}:${view.generation}:${view.attempt}`}
                  src={current.src}
                  alt={current.alt ?? labels.imageAlt(index + 1)}
                  className={styles.image}
                  data-lightbox-image=""
                  data-loaded={view.status === "loaded" || undefined}
                  style={{ width: imageWidth, height: imageHeight, left: imageLeft, top: imageTop }}
                  draggable={false}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  onLoad={(event) => {
                    if (
                      viewRef.current.identity !== identity ||
                      viewRef.current.generation !== view.generation
                    )
                      return;
                    const image = event.currentTarget;
                    setView((previous) =>
                      previous.identity === identity &&
                      previous.generation === view.generation &&
                      previous.attempt === view.attempt
                        ? {
                            ...previous,
                            width: image.naturalWidth,
                            height: image.naturalHeight,
                            status: image.naturalWidth > 0 ? "loaded" : "error",
                          }
                        : previous,
                    );
                  }}
                  onError={() => {
                    if (
                      viewRef.current.identity === identity &&
                      viewRef.current.generation === view.generation
                    )
                      setView((previous) =>
                        previous.identity === identity &&
                        previous.generation === view.generation &&
                        previous.attempt === view.attempt
                          ? { ...previous, status: "error" }
                          : previous,
                      );
                  }}
                />
              ) : null}
            </div>
            {view.status !== "loaded" ? (
              <div className={styles.status}>
                {view.status === "loading" ? (
                  <>
                    <span className={styles.spinner} aria-hidden="true" />
                    <p role="status">{labels.loading}</p>
                  </>
                ) : (
                  <>
                    <ImageBroken size={30} weight="light" aria-hidden="true" />
                    <p role="status">{labels.unavailable}</p>
                    {current && (current.src || onRetry) ? (
                      <button
                        type="button"
                        className={styles.retry}
                        disabled={view.retrying}
                        onClick={() => void retry()}
                      >
                        <ArrowClockwise size={16} aria-hidden="true" />
                        {view.retrying ? labels.retrying : labels.retry}
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </div>
          {items.length > 1 ? (
            <nav
              className={styles.navigation}
              aria-label={labels.imageLabel(index + 1, items.length)}
            >
              {action(
                "previous",
                !canPrevious,
                <button
                  type="button"
                  className={`${styles.button} ${styles.navigationButton}`}
                  disabled={!canPrevious}
                  aria-label={labels.previous}
                  data-lightbox-action="previous"
                  onClick={() => select(-1)}
                >
                  <CaretLeft size={23} aria-hidden="true" />
                </button>,
              )}
              {action(
                "next",
                !canNext,
                <button
                  type="button"
                  className={`${styles.button} ${styles.navigationButton}`}
                  disabled={!canNext}
                  aria-label={labels.next}
                  data-lightbox-action="next"
                  onClick={() => select(1)}
                >
                  <CaretRight size={23} aria-hidden="true" />
                </button>,
              )}
            </nav>
          ) : null}
          <footer className={styles.footer}>
            {current?.alt ? <p className={styles.caption}>{current.alt}</p> : null}
            <p className={styles.srOnly} aria-live="polite" aria-atomic="true">
              {current
                ? `${labels.imageLabel(index + 1, items.length)} · ${current.alt ?? labels.imageAlt(index + 1)}`
                : null}
            </p>
          </footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
